/**
 * Created-date range filter (§A) — a token-styled trigger + popover with quick
 * presets (Today / 7 days / 30 days) and two native date inputs for a custom
 * range. Emits YYYY-MM-DD bounds (local), either optional. Reuses the `.xselect`
 * trigger look; the panel is keyboard reachable (Esc / click-out close).
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";

/** Format a Date as local YYYY-MM-DD (no UTC shift). */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** A date N days before today (0 = today), local. */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
}

/** Human label for a date-only string (e.g. "Jun 7"). */
function pretty(s: string): string {
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface Preset {
  label: string;
  from: () => string;
  to: () => string;
}
const PRESETS: Preset[] = [
  { label: "Today", from: () => daysAgo(0), to: () => daysAgo(0) },
  { label: "Last 7 days", from: () => daysAgo(6), to: () => daysAgo(0) },
  { label: "Last 30 days", from: () => daysAgo(29), to: () => daysAgo(0) },
];

export function DateRangeField({
  from,
  to,
  onChange,
  ariaLabel = "Created date range",
}: {
  from?: string;
  to?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const baseId = useId();

  const summary = from && to ? `${pretty(from)} – ${pretty(to)}` : from ? `From ${pretty(from)}` : to ? `Until ${pretty(to)}` : "Any date";
  const active = Boolean(from || to);

  const close = useCallback((focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  const matchedPreset = (p: Preset) => active && from === p.from() && to === p.to();

  return (
    <div ref={rootRef} className="xselect daterange">
      <button
        ref={triggerRef}
        type="button"
        className="xselect__trigger control"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${ariaLabel}${active ? `: ${summary}` : ""}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.preventDefault();
            close();
          }
        }}
      >
        <span className={active ? "xselect__value" : "xselect__value xselect__value--placeholder"}>{summary}</span>
        <span className="xselect__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="xselect__menu daterange__panel"
          role="dialog"
          aria-label={ariaLabel}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              close();
            }
          }}
        >
          <div className="daterange__presets">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={"daterange__preset" + (matchedPreset(p) ? " daterange__preset--on" : "")}
                aria-pressed={matchedPreset(p)}
                onClick={() => onChange(p.from(), p.to())}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="daterange__custom">
            <label className="daterange__field">
              <span>From</span>
              <input
                type="date"
                className="control"
                value={from ?? ""}
                max={to || undefined}
                onChange={(e) => onChange(e.target.value || undefined, to)}
              />
            </label>
            <label className="daterange__field">
              <span>To</span>
              <input
                type="date"
                className="control"
                value={to ?? ""}
                min={from || undefined}
                onChange={(e) => onChange(from, e.target.value || undefined)}
              />
            </label>
          </div>
          <div className="xselect__footer">
            <button
              type="button"
              className="xselect__clear"
              disabled={!active}
              onClick={() => {
                onChange(undefined, undefined);
              }}
            >
              Clear
            </button>
            <button type="button" className="daterange__done" onClick={() => close()} id={`${baseId}-done`}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
